<template>
  <div class="regra-situacao">
    <p v-if="situacao.descricao" class="regra-situacao__descricao">
      {{ situacao.descricao }}
    </p>

    <!--
      Onde não há campo a escolher, dizer por quê. Ao lado dos gateways de
      frete, que oferecem a escolha, a ausência do seletor parecia
      esquecimento — e é o parceiro quem dita o dado.
    -->
    <p v-if="situacao.identificacao" class="identificacao">
      <fa-icon icon="info-circle" /> {{ situacao.identificacao }}
    </p>

    <div v-if="ligada && situacao.chaves_rastreio && situacao.chaves_rastreio.length" class="form-group chave-rastreio">
      <label :for="'chave-' + situacao.chave">Rastrear pelo</label>
      <v-select
        :id="'chave-' + situacao.chave"
        :options="situacao.chaves_rastreio"
        label="label"
        :clearable="false"
        :value="chaveEscolhida"
        @input="alterar({ chave_rastreio: $event && $event.value })"
      />
      <small class="campo-ajuda">
        O dado enviado ao gateway quando a consulta parte daqui.
      </small>
    </div>

    <div class="modo">
      <label class="modo__opcao" :class="{ 'modo__opcao--ativa': ligada && !temRegra }">
        <input type="radio" :name="grupoDeOpcoes" :checked="ligada && !temRegra" @change="limpar" />
        <span>
          <strong>Sempre</strong>
          <small>
            Sem regra: esta configuração responde por todos os casos deste
            momento. Mais de uma pode ser assim — todas são consultadas e os
            retornos aparecem juntos.
          </small>
        </span>
      </label>

      <label class="modo__opcao" :class="{ 'modo__opcao--ativa': ligada && temRegra }">
        <input type="radio" :name="grupoDeOpcoes" :checked="ligada && temRegra" @change="iniciar" />
        <span>
          <strong>Somente quando a regra abaixo for atendida</strong>
          <small>
            Entra apenas nos casos que a regra descreve, somando-se às que
            valem sempre e às outras cuja regra também casar. Todas são
            consultadas e os retornos aparecem juntos.
          </small>
        </span>
      </label>

      <label
        v-if="permiteDesligar"
        class="modo__opcao"
        :class="{ 'modo__opcao--ativa': !ligada }"
      >
        <input type="radio" :name="grupoDeOpcoes" :checked="!ligada" @change="desligar" />
        <span>
          <strong>Não usar nesta situação</strong>
          <small>
            A configuração continua valendo nos outros momentos. É o que permite
            consultar a nota em um serviço e o pedido em outro.
          </small>
        </span>
      </label>
    </div>

    <div v-if="ligada && temRegra" class="construtor">
      <AcaoObjetoCondicaoBuilder
        :condition-visual="condicao"
        :fields="situacao.campos"
        :fields-loaded="true"
        :operadores="operadores"
        :permite-sql="false"
        :dono-da-condicao="'em ' + situacao.nome.toLowerCase()"
        endpoint-base="/admin/integracoes"
        @update:conditionVisual="alterar({ condicao: $event })"
      />
    </div>
  </div>
</template>

<script>
/*
 * A regra de um momento específico da integração.
 *
 * A mesma integração é consultada em situações com vocabulários diferentes — a
 * Lincros cota durante a digitação do orçamento, rastreia a partir do pedido no
 * ERP e também a partir de uma nota — e uma regra escrita para um desses
 * momentos não faz sentido nos outros: "status igual a Faturado" é impossível
 * na cotação, "número da nota" não existe no orçamento.
 *
 * Por isso cada situação tem o seu bloco, com os campos que existem ali.
 */
import AcaoObjetoCondicaoBuilder from '@/components/AcaoObjeto/AcaoObjetoCondicaoBuilder';

export default {
  name: 'RegraSituacao',
  components: { AcaoObjetoCondicaoBuilder },
  props: {
    // {chave, nome, descricao, campos[]} vindo do catálogo.
    situacao: {
      type: Object,
      required: true,
    },
    condicao: {
      type: Object,
      default: null,
    },
    ativo: {
      type: Boolean,
      default: true,
    },
    operadores: {
      type: Array,
      default: () => [],
    },
    // Um valor de App\Constants\ChaveRastreio; vazio usa o padrão da situação.
    chaveRastreio: {
      type: String,
      default: null,
    },
    /*
     * Desligar um momento só faz sentido quando há outro para a configuração
     * continuar valendo. No conector consultado num momento só, isso seria
     * desativar a integração inteira — e pela porta errada: ela seguiria
     * marcada como ativa na lista e nunca seria escolhida, sem nada na tela
     * explicando por quê. Quem quer parar de usá-la tem o interruptor de
     * "Integração ativa", que é onde se vê o efeito.
     */
    permiteDesligar: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      /*
       * Ter regra é escolha do usuário, não consequência do conteúdo: deduzir
       * pelo que está montado faria o editor sumir no instante em que ele
       * abrisse a regra, antes de escrever a primeira condição.
       */
      aberta: false,
    };
  },
  computed: {
    chaveEscolhida() {
      const atual = this.chaveRastreio || this.situacao.chaves_rastreio[0].value;

      return this.situacao.chaves_rastreio.find((opcao) => opcao.value === atual) || null;
    },

    /*
     * Desligada só conta onde desligar é possível. Num conector de um momento
     * só, um `ativo: false` gravado antes desta regra deixaria a tela sem
     * nenhuma opção marcada — e a opção que o explicava não existe mais ali.
     */
    ligada() {
      return this.ativo || !this.permiteDesligar;
    },

    /*
     * Radios só se desmarcam entre si quando compartilham o `name`, e cada aba
     * tem o seu — senão a troca de situação levaria junto a escolha da anterior.
     */
    grupoDeOpcoes() {
      return 'modo-' + this.situacao.chave;
    },

    temRegra() {
      return this.aberta || this.temConteudo;
    },

    temConteudo() {
      return !!(this.condicao && Array.isArray(this.condicao.grupos) && this.condicao.grupos.length);
    },
  },
  methods: {
    /*
     * Um emit só, com a configuração inteira.
     *
     * Emitir campo a campo perdia alteração: dois `$emit` no mesmo tique leem
     * a prop antes de o pai reagir ao primeiro, e o segundo reescrevia o mapa
     * a partir do estado antigo — religar a situação e limpar a regra ao mesmo
     * tempo deixava a situação desligada.
     */
    alterar(mudancas) {
      this.$emit('update:configuracao', {
        ativo: this.ligada,
        condicao: this.condicao,
        chave_rastreio: this.chaveRastreio,
        ...mudancas,
      });
    },

    iniciar() {
      this.aberta = true;

      this.alterar({
        ativo: true,
        condicao: this.temConteudo
          ? this.condicao
          : { grupos: [{ logica: 'AND', condicoes: [] }] },
      });
    },

    limpar() {
      this.aberta = false;
      this.alterar({ ativo: true, condicao: null });
    },

    /*
     * Desligar não apaga a regra: quem religa a situação encontra o que tinha
     * montado, em vez de começar de novo.
     */
    desligar() {
      this.aberta = false;
      this.alterar({ ativo: false });
    },
  },
};
</script>

<style scoped>
.regra-situacao {
  padding: 4px 0 0;
}

.regra-situacao__cabecalho {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.regra-situacao__nome {
  display: block;
  font-size: 14px;
  color: #1d2939;
}

.regra-situacao__descricao {
  margin: 0 0 14px;
  font-size: 12px;
  color: #667085;
  line-height: 1.4;
}

.regra-situacao__estado {
  flex-shrink: 0;
  padding: 2px 10px;
  border-radius: 12px;
  background: #f2f4f7;
  color: #667085;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.regra-situacao__estado--regra {
  background: #eceaf3;
  color: #3a3e81;
}

.identificacao {
  display: flex;
  gap: 8px;
  margin: 0 0 14px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f2f4f7;
  color: #475467;
  font-size: 12px;
  line-height: 1.5;
}

.identificacao .fa-info-circle {
  margin-top: 2px;
  color: #98a2b3;
  flex-shrink: 0;
}

.chave-rastreio {
  max-width: 340px;
  margin-bottom: 14px;
}

.campo-ajuda {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #667085;
  line-height: 1.4;
}

.modo {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modo__opcao {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px 12px;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  cursor: pointer;
  margin: 0;
}

.modo__opcao--ativa {
  border-color: #3a3e81;
  background: #f2f2f8;
}

.modo__opcao strong {
  display: block;
  font-size: 13px;
  color: #344054;
}

.modo__opcao small {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: #667085;
  line-height: 1.4;
}

.construtor {
  margin-top: 12px;
}
</style>
